import { createContext, useEffect, useState } from 'react';
import axios from '../api/axiosInstance';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load the current user from backend
  const loadUserFromBackend = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAuthData(null);
      setCurrentGroup(null);
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get('/auth/me');
      setAuthData(res.data);
    } catch (err) {
      console.error('❌ Failed to fetch user in AuthContext:', err.response?.data || err.message);
      localStorage.removeItem('token');
      setAuthData(null);
      setCurrentGroup(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadUserFromBackend();
  }, []);

  // Whenever authData changes (login as different user, or re-fetch),
  // ensure currentGroup is valid for THAT user.
  useEffect(() => {
    if (!authData) {
      setCurrentGroup(null);
      return;
    }

    const userGroups = authData.groups || [];

    // If currentGroup is not set or it no longer belongs to the user, reset it
    if (
      !currentGroup ||
      !userGroups.some(g => g._id === currentGroup._id)
    ) {
      setCurrentGroup(userGroups[0] || null);
    }
  }, [authData]);

  // Call this from LoginForm AFTER you get a token
  const login = async (token) => {
    localStorage.setItem('token', token);
    setLoading(true);
    await loadUserFromBackend(); // always pull fresh, populated user & groups

  };

  const logout = () => {
    localStorage.removeItem('token');
    setAuthData(null);
    setCurrentGroup(null);
  };

  // 🕒 Auto-Logout on Inactivity (30 Minutes)
  useEffect(() => {
    if (!authData) return;

    const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 mins
    let timer;

    const resetTimer = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        console.warn('💤 User inactive for 30m. Auto-logging out.');
        logout();
      }, INACTIVITY_LIMIT);
    };

    // Listeners for activity
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('click', resetTimer);

    resetTimer(); // Start timer immediately

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('click', resetTimer);
    };
  }, [authData]);

  if (loading) return <div>Loading...</div>;

  return (
    <AuthContext.Provider
      value={{
        authData,
        setAuthData,
        currentGroup,
        setCurrentGroup,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
